import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { getProductById, products } from '../data/products';
import { ShieldCheckIcon, ArrowDownTrayIcon, HomeIcon, ChevronRightIcon, CreditCardIcon, InformationCircleIcon, StarIcon, DocumentTextIcon, SparklesIcon, CalendarDaysIcon, ComputerDesktopIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { getProductDownloadLink } from '../utils/productLinks';
import { initializeRazorpay, createRazorpayOrder, processRazorpayPayment, verifyRazorpayPayment } from '../utils/razorpay';
import { addPurchasedProduct, setLoading, setError, clearError, selectIsProductPurchased, selectIsLoading } from '../store/store';

import { ProductStructuredData } from './SEO/StructuredData';
import ProductCard from './ProductCard';
import Reviews from './Reviews';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const product = getProductById(id);
  const isProductPurchased = useSelector(state => selectIsProductPurchased(state, parseInt(id)));
  const isLoading = useSelector(selectIsLoading);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [activeImage, setActiveImage] = useState(product?.image);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Initialize Razorpay
    const initRazorpay = async () => {
      const loaded = await initializeRazorpay();
      setRazorpayLoaded(loaded);
    };
    initRazorpay();
    
    if (product) {
      setActiveImage(product.image);
    }
  }, [id, product]);

  // Get related products (same category, excluding current product)
  const relatedProducts = products
    .filter(p => p.category === product?.category && p.id !== product?.id)
    .slice(0, 4);

  const handlePayment = async () => {
    if (!razorpayLoaded) {
      toast.error('Payment system is loading. Please try again.');
      return;
    }

    dispatch(setLoading(true));
    dispatch(clearError());
    
    try {
      const orderData = await createRazorpayOrder(product.price, 'INR', `product_${product.id}`);
      
      await processRazorpayPayment(
        orderData,
        product,
        async (response) => {
          try {
            const verification = await verifyRazorpayPayment(
              response.razorpay_payment_id,
              response.razorpay_order_id,
              response.razorpay_signature
            );

            if (verification.success) {
              dispatch(addPurchasedProduct(parseInt(product.id)));
              // Meta Pixel Purchase event (client-side)
              if (typeof window !== 'undefined' && window.fbq) {
                try {
                  window.fbq('track', 'Purchase', { value: product.price, currency: 'INR' });
                } catch (e) {
                  console.warn('Meta Pixel tracking error:', e);
                }
              }
              toast.success('Payment successful! Opening download link...');
              const downloadLink = getProductDownloadLink(product.id);
              if (downloadLink) {
                window.open(downloadLink, '_blank');
              } else {
                toast.error('Download link not available. Please contact support.');
              }
            } else {
              toast.error('Payment verification failed.');
              dispatch(setError('Payment verification failed.'));
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error('Payment verification failed.');
            dispatch(setError('Payment verification failed.'));
          }
        },
        (error) => {
          toast.error(error || 'Payment failed. Please try again.');
          dispatch(setError(error || 'Payment failed. Please try again.'));
        }
      );
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment failed. Please try again.');
      dispatch(setError('Payment failed. Please try again.'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleDownload = () => {
    const downloadLink = getProductDownloadLink(product.id);
    if (downloadLink) {
      window.open(downloadLink, '_blank');
    } else {
      toast.error('Download link not available. Please contact support.');
    }
  };

  if (!product) {
    return (
      <>
        <Helmet>
          <title>Pingfence | Product Not Found</title>
          <meta name="description" content="The requested product could not be found." />
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <div className="container py-12 text-center">
          <h2 className="text-2xl font-bold mb-4">Product not found</h2>
          <button
            onClick={() => navigate('/products')}
            className="btn btn-primary"
          >
            Back to Products
          </button>
        </div>
      </>
    );
  }

  // Generate Included Items based on Category (Moved logic here for cleaner render)
  const generateIncludedItems = () => {
    const productName = product.name.toLowerCase();
    const category = product.category.toLowerCase();
    
    if (productName.includes('python') || category.includes('python')) {
      return ["Complete Python source code files", "Requirements.txt with all dependencies", "Virtual environment setup guide", "Step-by-step execution tutorials", "Error handling & debugging guides", "Performance optimization techniques", "Unit testing examples & frameworks", "Documentation & inline comments", "Sample datasets & test files", "Deployment instructions"];
    } else if (productName.includes('cybersecurity') || category.includes('security')) {
      return ["Comprehensive security training modules", "Hands-on lab exercises & scenarios", "Real-world case studies", "Security tool configurations", "Penetration testing frameworks", "Incident response playbooks", "Threat hunting methodologies", "Security assessment templates", "Compliance checklists", "Risk assessment frameworks"];
    } else if (productName.includes('business') || category.includes('business')) {
      return ["Professional business templates", "Financial planning spreadsheets", "Business model canvases", "Strategic planning frameworks", "Market analysis templates", "Competitive analysis tools", "Sales forecasting models", "Budget planning worksheets", "Cash flow projections", "Investment proposal templates"];
    } else {
      return ["Complete source files & templates", "Detailed documentation & guides", "Step-by-step tutorials", "Ready-to-use frameworks", "Configuration files", "Sample data & examples", "Video demonstrations", "Best practices guides", "Troubleshooting resources", "Premium Support"];
    }
  };

  return (
    <>
      <Helmet>
        <title>Pingfence | {product.name}</title>
        <meta name="description" content={product.shortDescription} />
        <link rel="canonical" href={`https://pingfence.vercel.app/product/${product.id}`} />
      </Helmet>
      <ProductStructuredData product={product} />
      
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-28 lg:pb-8">
        {/* Breadcrumbs */}
        <nav className="flex flex-wrap items-center space-x-2 text-sm text-gray-800 mb-6">
          <Link to="/" className="hover:text-primary transition-colors">
            <HomeIcon className="h-4 w-4" />
          </Link>
          <ChevronRightIcon className="h-3 w-3" />
          <Link to="/products" className="hover:text-primary transition-colors">Products</Link>
          <ChevronRightIcon className="h-3 w-3" />
          <Link to={`/products?category=${encodeURIComponent(product.category)}`} className="hover:text-primary transition-colors">{product.category}</Link>
          <ChevronRightIcon className="h-3 w-3" />
          <span className="text-black font-bold">{product.name}</span>
        </nav>

        {/* Two Column Layout Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
          
          {/* LEFT COLUMN: Media & Content */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* Title for Mobile Only */}
            <div className="lg:hidden mb-4">
              <h1 className="text-3xl font-bold text-black mb-2">{product.name}</h1>
              <p className="text-sm text-gray-800">By Pingfence Official</p>
            </div>

            {/* Main Media Gallery */}
            <div className="space-y-4">
              {/* Big Image */}
              <div className="bg-white border-2 border-black aspect-video flex items-center justify-center shadow-[6px_6px_0px_0px_#000000]">
                <img
                  src={activeImage}
                  alt={product.name}
                  onError={() => setActiveImage(product.image)}
                  className="w-full h-full object-cover filter contrast-125 saturate-150"
                />
              </div>
              
              {/* Main image plus five replaceable supporting images */}
              <div className="grid grid-cols-5 gap-2 sm:gap-4">
                {product.gallery.map((imagePath, idx) => (
                  <div 
                    key={idx} 
                    onClick={() => setActiveImage(imagePath)}
                    className={`cursor-pointer border-2 transition-all aspect-video ${activeImage === imagePath ? 'border-black shadow-[4px_4px_0px_0px_#9FE240]' : 'border-black opacity-60 hover:opacity-100 hover:shadow-[4px_4px_0px_0px_#000000]'}`}
                  >
                    <img
                      src={imagePath}
                      alt={`${product.name} thumbnail ${idx + 1}`}
                      onError={(event) => { event.currentTarget.src = product.image; }}
                      className="w-full h-full object-cover filter contrast-125 saturate-150"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Content Tabs area (Simulated stacking) */}
            <div className="bg-white border-2 border-black p-4 sm:p-6 md:p-8 space-y-12 shadow-[6px_6px_0px_0px_#000000]">
              
              {/* Description */}
              <section className="prose prose-invert max-w-none">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <DocumentTextIcon className="h-6 w-6 text-primary" />
                  Overview
                </h2>
                <div className="text-black font-medium leading-relaxed whitespace-pre-line">
                  {product.description}
                </div>
              </section>

              <hr className="border-primary/10" />

              {/* Key Features */}
              <section>
                <h2 className="text-2xl font-black uppercase tracking-tight mb-6 flex items-center gap-2 text-black">
                  <SparklesIcon className="h-6 w-6 text-primary" />
                  Key Features
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {product.features?.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3 bg-gray-100 border-2 border-black p-4 shadow-[2px_2px_0px_0px_#9FE240]">
                      <ShieldCheckIcon className="h-5 w-5 text-black flex-shrink-0 mt-0.5" />
                      <span className="text-black font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </section>

              <hr className="border-black border-2" />

              {/* Specifications */}
              {product.specifications && Object.keys(product.specifications).length > 0 && (
                <section>
                  <h2 className="text-2xl font-black uppercase tracking-tight mb-6 flex items-center gap-2 text-black">
                    <InformationCircleIcon className="h-6 w-6 text-primary" />
                    Technical Details
                  </h2>
                  <div className="bg-white border-2 border-black">
                    <table className="w-full text-left text-sm text-black">
                      <tbody>
                        {Object.entries(product.specifications).map(([key, value], idx) => (
                          <tr key={key} className={idx % 2 === 0 ? 'bg-gray-100' : 'bg-white'}>
                            <th className="px-6 py-4 font-bold capitalize text-black w-1/3 border-r-2 border-black border-b-2">
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </th>
                            <td className="px-6 py-4 border-b-2 border-black font-medium">{value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              <hr className="border-black border-2" />

              {/* What's Included */}
              <section>
                <h2 className="text-2xl font-black uppercase tracking-tight mb-6 flex items-center gap-2 text-black">
                  <DocumentTextIcon className="h-6 w-6 text-primary" />
                  What's Included
                </h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {generateIncludedItems().map((item, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 bg-gray-100 border-2 border-black shadow-[2px_2px_0px_0px_#9FE240]">
                      <div className="w-2 h-2 bg-black flex-shrink-0" />
                      <span className="text-black font-bold text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </section>

            </div>

            {/* Reviews Section */}
            <div className="bg-white border-2 border-black p-6 md:p-8 shadow-[6px_6px_0px_0px_#000000]" id="reviews">
              <h2 className="text-3xl font-black uppercase tracking-tight mb-8 text-black" style={{textShadow: '2px 2px 0px #9FE240'}}>Customer Reviews</h2>
              <Reviews productId={parseInt(id)} />
            </div>

          </div>

          {/* RIGHT COLUMN: Sticky Sidebar Actions */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              
              {/* Product Header Card */}
              <div className="bg-white border-2 border-black p-6 shadow-[6px_6px_0px_0px_#000000]">
                
                {/* Title & Meta (Desktop) */}
                <div className="hidden lg:block mb-6">
                  <h1 className="text-3xl xl:text-4xl font-black text-black mb-2 leading-tight uppercase tracking-tighter">{product.name}</h1>
                  <p className="inline-block bg-primary border-2 border-black px-2 py-1 text-sm text-black font-bold hover:translate-x-1 hover:-translate-y-1 hover:shadow-[2px_2px_0px_0px_#000000] cursor-pointer transition-all">Pingfence Official</p>
                  
                  {/* Rating summary */}
                  <div className="flex items-center gap-2 mt-3 text-sm">
                    <div className="flex text-yellow-500">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <StarIcon key={star} className="h-4 w-4 fill-current" />
                      ))}
                    </div>
                    <a href="#reviews" className="text-gray-800 hover:text-black transition-colors">(12 Reviews)</a>
                  </div>
                </div>

                {/* Pricing & CTA */}
                <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-4 border-black p-4 pb-8 shadow-[0_-4px_0px_0px_#000000] lg:static lg:bg-transparent lg:border-none lg:p-0 lg:shadow-none mb-0 lg:mb-6 lg:pb-6 border-b-0 lg:border-b-2 border-black">
                  <div className="flex items-center justify-between lg:flex-col lg:items-start lg:gap-0 gap-4 mb-0 lg:mb-6 max-w-7xl mx-auto">
                    <div className="flex flex-col lg:flex-row lg:items-baseline gap-1 lg:gap-2">
                      <span className="text-3xl lg:text-5xl font-black text-black" style={{textShadow: '2px 2px 0px #9FE240'}}>
                        ₹{product.price.toLocaleString('en-IN')}
                      </span>
                      <span className="hidden lg:inline-block text-sm font-bold text-gray-800 bg-gray-100 border-2 border-black px-2 py-0.5">Tax included</span>
                    </div>

                    <div className="flex-1 lg:w-full">
                      {isProductPurchased ? (
                        <button 
                          onClick={handleDownload}
                          className="btn bg-black text-primary border-2 border-black w-full flex items-center justify-center space-x-2 shadow-[4px_4px_0px_0px_#9FE240] hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[6px_6px_0px_0px_#9FE240] py-3 lg:py-3"
                        >
                          <ArrowDownTrayIcon className="h-5 w-5 lg:h-6 lg:w-6" />
                          <span className="text-sm lg:text-base">Download</span>
                        </button>
                      ) : (
                        <button 
                          onClick={handlePayment}
                          disabled={isLoading || !razorpayLoaded}
                          className="btn btn-primary w-full flex items-center justify-center space-x-2 text-base lg:text-lg py-3 lg:py-3"
                        >
                          {isLoading ? (
                            <>
                              <div className="animate-spin h-5 w-5 border-b-2 border-black"></div>
                              <span>Processing...</span>
                            </>
                          ) : (
                            <>
                              <CreditCardIcon className="h-5 w-5 lg:h-6 lg:w-6 font-bold" />
                              <span>Buy Now</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="hidden lg:flex text-center text-xs text-black font-bold mt-4 items-center justify-center gap-1">
                    <ShieldCheckIcon className="h-4 w-4" /> Secure Razorpay transaction
                  </p>
                </div>

                {/* Metadata List */}
                <div className="space-y-4 text-sm">
                  <div className="flex justify-between items-center text-black font-medium">
                    <div className="flex items-center gap-2 text-gray-800">
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      Delivery
                    </div>
                    <span className="font-medium text-black">Instant Download</span>
                  </div>
                  <div className="flex justify-between items-center text-black font-medium">
                    <div className="flex items-center gap-2 text-gray-800">
                      <ComputerDesktopIcon className="h-4 w-4" />
                      Access
                    </div>
                    <span className="font-medium text-black">Lifetime</span>
                  </div>
                  <div className="flex justify-between items-center text-black font-medium">
                    <div className="flex items-center gap-2 text-gray-800">
                      <DocumentTextIcon className="h-4 w-4" />
                      License
                    </div>
                    <span className="font-medium text-primary hover:underline cursor-pointer">Standard License</span>
                  </div>
                  <div className="flex justify-between items-center text-black font-medium">
                    <div className="flex items-center gap-2 text-gray-800">
                      <CalendarDaysIcon className="h-4 w-4" />
                      Updated
                    </div>
                    <span className="font-medium text-black">Recently</span>
                  </div>
                </div>

              </div>

              {/* Support Card */}
              <div className="bg-gray-100 border-2 border-black p-5 shadow-[4px_4px_0px_0px_#000000]">
                <h3 className="font-black uppercase tracking-wide text-black mb-2">Need Help?</h3>
                <p className="text-sm font-medium text-black mb-4">If you encounter any issues or have questions about this asset, our team is ready to assist.</p>
                <Link to="/contact" className="btn btn-primary inline-flex text-sm py-2 px-4">Contact Support &rarr;</Link>
              </div>

            </div>
          </div>
        </div>

        {/* Related Products at the bottom */}
        {relatedProducts.length > 0 && (
          <div className="mt-20 border-t border-primary/10 pt-16">
            <div className="flex justify-between items-end mb-8">
              <h2 className="text-2xl font-bold">More from this Category</h2>
              <Link to={`/products?category=${encodeURIComponent(product.category)}`} className="text-primary hover:underline text-sm font-medium">View All</Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map(relatedProduct => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProductDetail;